import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors } from '@angular/forms';
import { catchError, map, Observable, of, switchMap, timer } from 'rxjs';
import { UUID } from 'symbols';
import {
  CreateStorageLocationModel,
  ImportStorageResponse,
  MoveStorageModel,
  NomenclatureModel,
  StorageLocationModel,
  StorageLocationTreeModel
} from '../symbols';

@Injectable()
export class StorageService {
  private readonly apiUrl = '/api/storage/';

  constructor(private http: HttpClient) {
  }

  getStorageLocations(folderId?: string): Observable<StorageLocationTreeModel> {
    const params: any = {};

    if (folderId) {
      params['id'] = folderId;
    }
    return this.http.get<StorageLocationTreeModel>(`${this.apiUrl}locations`, { params });
  }

  getAllStorageLocations(): Observable<StorageLocationModel[]> {

    return this.http.get<StorageLocationModel[]>(`${this.apiUrl}all-locations`);
  }

  getGoods(search?: string, onlyUsed?: boolean): Observable<NomenclatureModel[]> {
    const params: any = {};
    if (search) {
      params['search'] = search;
    }
    if( onlyUsed ) {
      params['onlyUsed'] = onlyUsed;
    }
    return this.http.get<NomenclatureModel[]>(`${this.apiUrl}goods`, { params });
  }

  createStorageItem(item: NomenclatureModel): Observable<NomenclatureModel> {
    return this.http.post<NomenclatureModel>(`${this.apiUrl}item/`, item);
  }

  updateStorageItem(id: UUID, item: NomenclatureModel): Observable<NomenclatureModel> {
    return this.http.patch<NomenclatureModel>(`${this.apiUrl}item/${id}/`, item);
  }

  moveItems(data: MoveStorageModel): Observable<StorageLocationTreeModel> {
    return this.http.patch<StorageLocationTreeModel>(`${this.apiUrl}move/`, data);
  }

  createStorageLocation(item: CreateStorageLocationModel): Observable<StorageLocationModel> {
    return this.http.post<StorageLocationModel>(`${this.apiUrl}location/`, item).pipe(
      map(f => ({ ...f, isEmpty: true }))
    );
  }

  updateStorageLocation(id: UUID, item: CreateStorageLocationModel): Observable<StorageLocationModel> {
    return this.http.patch<StorageLocationModel>(`${this.apiUrl}location/${id}/`, item);
  }

  checkArticleIsAvailable(article: string, id?: string): Observable<boolean> {
    return this.http.get<{ allowed: boolean }>(`${this.apiUrl}validate-by-article/${article}/${id || 'creating'}/`)
      .pipe(map(res => res.allowed));
  }

  validateArticle(debounceTime = 500): (control: AbstractControl) => Observable<ValidationErrors | null> {
    return (control: AbstractControl) => timer(debounceTime)
      .pipe(
        switchMap(() => this.checkArticleIsAvailable(control.value, control.parent?.value?.id)),
        map((result) => (result ? null : { allowed: true })),
        catchError(() => of(null))
      );
  }

  checkXLS(file: File, onlyUpdate?: boolean, prefix?: string): Observable<ImportStorageResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('onlyUpdate', String(!!onlyUpdate));
    if (prefix) {
      formData.append('prefix', prefix);
    }

    return this.http.post<ImportStorageResponse>(`${this.apiUrl}check-xls`, formData);
  }

  uploadXLS(file: File, onlyUpdate?: boolean, prefix?: string, locationId?: string): Observable<ImportStorageResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('onlyUpdate', String(!!onlyUpdate));
    if (prefix) {
      formData.append('prefix', prefix);
    }
    if (locationId) {
      formData.append('locationId', locationId);
    }

    return this.http.post<ImportStorageResponse>(`${this.apiUrl}update-storage`, formData);
  }
}
